import React, {useState, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom'; // Updated import
import {useAuth, useNotification} from '../../App';
import {
    Mail,
    CheckCircle,
    AlertCircle,
    Loader,
    ArrowRight,
    Home,
    Shield,
    Clock,
    RefreshCw
} from 'lucide-react';
import {API_CONSTANTS} from "../../Constants";
import SEO from "../../Components/Common/SEO";

const EmailVerification = () => {
    const navigate = useNavigate();
    const location = useLocation(); // Use useLocation to access query string
    const {establishSession} = useAuth();
    const {addNotification} = useNotification();
    const [status, setStatus] = useState('verifying');
    const [isLoading, setIsLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [countdown, setCountdown] = useState(0);

    // Extract token from query string
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token'); // Get the token from the query string

    useEffect(() => {
        if (token) {
            verifyEmail();
        } else {
            setStatus('error');
            setIsLoading(false);
            addNotification('No verification token provided.', 'error');
        }
    }, [token]);

    const verifyEmail = async () => {
        try {
            const response = await fetch(`${API_CONSTANTS.BASE_URL}/auth/verify-email`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({token})
            });
            const data = await response.json();

            if (data.success) {
                setStatus('success');
                addNotification('Email verified successfully!', 'success');
                await establishSession(data.data.user, {
                    accessToken: data.data.accessToken,
                    refreshToken: data.data.refreshToken
                });
                setTimeout(() => {
                    navigate('/');
                }, 3000);

            } else {
                setStatus(data.error.includes('expired') ? 'expired' : 'error');
                setEmail('user@example.com');
                addNotification(data.error || 'Verification failed.', 'error');
            }
        } catch (error) {
            setStatus('error');
            setEmail('user@example.com');
            addNotification('Verification failed. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (countdown > 0) return;

        setIsLoading(true);
        try {
            const response = await fetch(`${API_CONSTANTS.BASE_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({email})
            });
            const data = await response.json();

            if (data.success) {
                addNotification('Verification email resent!', 'success');
                setCountdown(60);
                const timer = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                addNotification('Failed to resend email.', 'error');
            }
        } catch (error) {
            addNotification('Failed to resend email.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-karibu-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <SEO title="Verify Email" canonicalPath="/verify-email" noindex />
            {/* Rest of your JSX remains unchanged */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-highland-50 to-karibu-200"/>
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }}/>
            </div>

            <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="text-center">
                    <div
                        className="mx-auto h-16 w-16 bg-highland-500 rounded-2xl flex items-center justify-center mb-6">
                        <Mail className="h-8 w-8 text-white"/>
                    </div>
                    <h2 className="font-display text-3xl font-bold text-soil-900">Verify Your Email</h2>
                    <p className="mt-2 text-sm text-soil-600">
                        Confirm your email address to activate your account
                    </p>
                </div>
            </div>

            <div className="relative z-10 mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-xl rounded-xl sm:px-10">
                    {isLoading ? (
                        <div className="text-center">
                            <Loader className="w-12 h-12 mx-auto animate-spin text-highland-500 mb-4"/>
                            <p className="text-soil-600">Verifying your email...</p>
                        </div>
                    ) : status === 'success' ? (
                        <div className="text-center">
                            <div
                                className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-highland-50 mb-6">
                                <CheckCircle className="h-8 w-8 text-highland-500"/>
                            </div>
                            <h3 className="font-display text-lg font-medium text-soil-900 mb-2">Email Verified!</h3>
                            <p className="text-sm text-soil-600 mb-6">
                                Your email has been verified. You'll be redirected to the homepage shortly.
                            </p>
                            <button
                                onClick={() => navigate('/')}
                                className="w-full bg-highland-500 hover:bg-highland-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
                            >
                                Go to Homepage
                            </button>
                            <p className="mt-4 text-sm text-soil-400">
                                Redirecting in 3 seconds...
                            </p>
                        </div>
                    ) : status === 'expired' ? (
                        <div className="text-center">
                            <div
                                className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-murram-100/40 mb-6">
                                <Clock className="h-8 w-8 text-murram-500"/>
                            </div>
                            <h3 className="font-display text-lg font-medium text-soil-900 mb-2">Verification Link Expired</h3>
                            <p className="text-sm text-soil-600 mb-6">
                                The verification link has expired. Please request a new one.
                            </p>
                            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4 mb-6">
                                <div className="flex items-start">
                                    <Shield className="h-5 w-5 text-highland-500 mt-0.5 mr-3"/>
                                    <div className="text-left">
                                        <h4 className="text-sm font-medium text-highland-700 mb-1">Security Note:</h4>
                                        <p className="text-sm text-highland-600">
                                            Verification links expire after 24 hours for security reasons.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={handleResend}
                                disabled={countdown > 0 || isLoading}
                                className="w-full bg-soil-100 hover:bg-soil-100/70 disabled:bg-soil-50 disabled:text-soil-400 text-soil-600 font-medium py-3 px-4 rounded-lg transition-colors duration-200 mb-4"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader className="w-4 h-4 mr-2 animate-spin inline"/>
                                        Sending...
                                    </>
                                ) : countdown > 0 ? (
                                    `Resend in ${countdown}s`
                                ) : (
                                    <>
                                        <RefreshCw className="w-4 h-4 mr-2"/>
                                        Resend Verification Email
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full bg-highland-500 hover:bg-highland-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200"
                            >
                                Back to Login
                            </button>
                        </div>
                    ) : (
                        <div className="text-center">
                            <div
                                className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-murram-100/40 mb-6">
                                <AlertCircle className="h-8 w-8 text-murram-500"/>
                            </div>
                            <h3 className="font-display text-lg font-semibold text-soil-900 mb-2">Verification Failed</h3>
                            <p className="text-sm text-soil-600 mb-6">
                                The verification could not be completed. Please try again or request a new link.
                            </p>
                            <button
                                onClick={handleResend}
                                disabled={countdown > 0 || isLoading}
                                className="w-full bg-soil-100 hover:bg-soil-100/70 disabled:bg-soil-50 disabled:text-soil-400 text-soil-600 font-semibold py-3 px-4 rounded-lg transition-colors duration-200 mb-4"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader className="w-4 h-4 mr-2 animate-spin inline"/>
                                        Sending...
                                    </>
                                ) : countdown > 0 ? (
                                    `Resend in ${countdown}s`
                                ) : (
                                    <>
                                        <RefreshCw className="w-4 h-4 mr-2"/>
                                        Resend Verification Email
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full bg-highland-500 hover:bg-highland-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200"
                            >
                                Back to Login
                            </button>
                        </div>
                    )}
                </div>

                <div className="mt-6 text-center">
                    <button
                        onClick={() => navigate('/')}
                        className="inline-flex items-center text-sm text-soil-400 hover:text-soil-600"
                    >
                        <ArrowRight className="w-4 h-4 mr-1 rotate-180"/>
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;